import { useCallback, useEffect, useRef, useState } from 'react';
import type { ContactSyncView } from '@/api/generated';
import { ApiError } from '@/api/errors';
import { useTenantApi } from '@/tenant/useTenant';

export interface CrmState {
  status: 'loading' | 'ready' | 'error';
  data: ContactSyncView | null;
  error: ApiError | null;
  resyncing: boolean;
  reload: () => void;
  resync: () => Promise<void>;
}

function toApiError(err: unknown): ApiError {
  return err instanceof ApiError ? err : new ApiError('UNKNOWN', 0, {});
}

/** Loads CRM / contact-sync status. Resync POSTs then refetches GET. */
export function useCrm(): CrmState {
  const [status, setStatus] = useState<CrmState['status']>('loading');
  const [data, setData] = useState<ContactSyncView | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [resyncing, setResyncing] = useState(false);
  const api = useTenantApi();
  const started = useRef(false);

  const reload = useCallback(() => {
    setStatus('loading');
    setError(null);
    api.getCrm().then(
      (next) => {
        setData(next);
        setError(null);
        setStatus('ready');
      },
      (err: unknown) => {
        setData(null);
        setError(toApiError(err));
        setStatus('error');
      },
    );
  }, [api]);

  const resync = useCallback(async () => {
    setResyncing(true);
    try {
      await api.resyncCrm();
      const next = await api.getCrm();
      setData(next);
      setError(null);
      setStatus('ready');
    } finally {
      setResyncing(false);
    }
  }, [api]);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    reload();
  }, [reload]);

  return { status, data, error, resyncing, reload, resync };
}
